import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import {
  ColorValues,
  Colors,
  Flex,
  IconShare,
  IconWonFilled,
  Typography,
} from '@banhala/ui-kit';
import { scheduleOnRN } from 'react-native-worklets';
import ReAnimated, {
  useAnimatedProps,
  useAnimatedReaction,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { renderHTMLToText } from 'shared/src/utils/parseHTMLToReactNative/parseHTMLToReactNative';
import { isAndroid } from 'shared/src/utils';
import {
  SCROLL_ANIMATION_DURATION,
  SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
} from '../../constant/scrollValue';
import { ScrollContext } from '../../context/ScrollContext';

const ICON_SIZE = 18;
const ANIMATION_DURATION = 200;
const TOGGLE_INTERVAL = 1200;

interface AffiliateFloatingButtonProps {
  text: string;
  marginBottom: number;
  onPress: () => void;
  onView: () => void;
}

export const AffiliateFloatingButton = ({
  text,
  marginBottom,
  onPress,
  onView,
}: AffiliateFloatingButtonProps) => {
  const { scrollY } = useContext(ScrollContext);

  const rewardOpacity = useRef(new Animated.Value(0)).current;
  const shareOpacity = useRef(new Animated.Value(1)).current;
  const isLogViewRef = useRef(false);
  const showReward = useRef(true);

  const handleView = useCallback(() => {
    if (!isLogViewRef.current) {
      isLogViewRef.current = true;
      onView();
    }
  }, [onView]);

  const animatedStyle = useAnimatedStyle(() => ({
    position: 'absolute',
    bottom: marginBottom + 16 + 32,
    left: '50%',
    transform: [{ translateX: '-50%' }],
    opacity: scrollY
      ? isAndroid
        ? scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
          ? 1
          : 0
        : withTiming(
            scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE ? 1 : 0,
            {
              duration: SCROLL_ANIMATION_DURATION,
            },
          )
      : 0,
  }));

  const animatedProps = useAnimatedProps(() => ({
    pointerEvents:
      scrollY && scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
        ? ('auto' as const)
        : ('none' as const),
  }));

  useAnimatedReaction(
    () => scrollY && scrollY.value > SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
    (isVisible, prevIsVisible) => {
      if (isVisible && !prevIsVisible) {
        scheduleOnRN(handleView);
      }
    },
  );

  useEffect(() => {
    const interval = setInterval(() => {
      showReward.current = !showReward.current;
      const nextShowReward = showReward.current;

      Animated.parallel([
        Animated.timing(rewardOpacity, {
          toValue: nextShowReward ? 1 : 0,
          duration: ANIMATION_DURATION,
          useNativeDriver: true,
        }),
        Animated.timing(shareOpacity, {
          toValue: nextShowReward ? 0 : 1,
          duration: ANIMATION_DURATION,
          useNativeDriver: true,
        }),
      ]).start();
    }, TOGGLE_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReAnimated.View style={animatedStyle} animatedProps={animatedProps}>
      <Flex.Row
        onPress={onPress}
        alignItems={'center'}
        gap={4}
        padding={{ horizontal: 14, vertical: 10 }}
        backgroundColorValue={ColorValues.white}
        borderRadius={50}
        shadowStep={'large'}
      >
        <View style={styles.iconArea}>
          <Animated.View
            style={[
              StyleSheet.absoluteFill,
              styles.iconCenter,
              { opacity: shareOpacity },
            ]}
          >
            <IconShare size={ICON_SIZE} fill={Colors.content_secondary} />
          </Animated.View>
          <Animated.View
            style={[
              StyleSheet.absoluteFill,
              styles.iconCenter,
              { opacity: rewardOpacity },
            ]}
          >
            <IconWonFilled size={ICON_SIZE} fill={ColorValues.orange30} />
          </Animated.View>
        </View>
        <Typography type="subtitle3" color="content_secondary">
          {renderHTMLToText(text)}
        </Typography>
      </Flex.Row>
    </ReAnimated.View>
  );
};

const styles = StyleSheet.create({
  iconArea: {
    width: ICON_SIZE,
    height: ICON_SIZE,
  },
  iconCenter: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
